<template>
  <section>
    <h4 class="mb-4">
      {{
        $t("labels.generic", {
          pt: "Engajamento de alunos",
          en: "Student engagement",
        })
      }}
    </h4>
    <main class="mb-10">
      <div class="bg-white rounded-lg border border-gray-300">
        <Table
          class="mb-80"
          :actions="actions"
          :columns="columns"
          :slotData="data"
          :loading="loading"
          :hasStatus="false"
          :rows="params.perPage"
          :totalRecords="params.total"
          :params="params"
          @onGoTo="openModal"
          @onPaginate="paginate"
          @onFilter="applyFilter"
          :actionsTitle="
            $t('labels.generic', { pt: 'Detalhes', en: 'Details' })
          "
        >
          <template #filters>
            <InputText
              v-if="filterType === 'user_name'"
              v-model="filters.search"
              :placeholder="
                $t('labels.generic', {
                  pt: 'Filtrar por nome',
                  en: 'Filter by name',
                })
              "
              class="w-full"
              @input="onFilterChange"
            />
          </template>
        </Table>
      </div>
    </main>
    <Dialog
      v-model:visible="showDetails"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '40vw' }"
      class="custom-modal"
      :modal="true"
    >
      <template #header>
        <h5 v-for="(item, index) in checked" :key="index">
          <span class="font-bold">
            {{
              $t("labels.generic", {
                pt: "Informações do aluno -",
                en: "Student information -",
              })
            }}
          </span>
          {{ item.value }}
        </h5>
      </template>
      <div class="my-5">
        <div class="gap-7">
          <h5>
            {{
              $t("labels.generic", {
                pt: "Sobre:",
                en: "About:",
              })
            }}
          </h5>
          <p v-for="(item, index) in info" :key="index">
            <strong>{{ item.name }}:</strong> {{ item.value }}
          </p>
          <h5 class="mt-2">
            {{
              $t("labels.generic", {
                pt: "Curso:",
                en: "Course:",
              })
            }}
          </h5>
          <p v-for="(item, index) in course" :key="index">
            <strong>{{ item.name }}:</strong> {{ item.value }}
            <span v-if="item.subtitle !== undefined">
              <i
                :class="[
                  'pi',
                  item.subtitle > 0
                    ? 'pi-chevron-up text-green-500'
                    : item.subtitle < 0
                    ? 'pi-chevron-down text-red-500'
                    : 'pi-minus text-gray-500',
                ]"
                class="ml-2 align-middle"
              ></i>
            </span>
          </p>
          <h5 class="mt-2">
            {{
              $t("labels.generic", {
                pt: "Dados Matemáticos:",
                en: "Mathematical Data:",
              })
            }}
          </h5>
          <p v-for="(item, index) in math" :key="index">
            <strong>{{ item.name }}:</strong> {{ item.value }}
            <span v-if="item.subtitle !== undefined">
              <i
                :class="[
                  'pi',
                  item.subtitle > 0
                    ? 'pi-chevron-up text-green-500'
                    : item.subtitle < 0
                    ? 'pi-chevron-down text-red-500'
                    : 'pi-minus text-gray-500',
                ]"
                class="ml-2 align-middle"
              ></i>
            </span>
          </p>
        </div>
      </div>
      <div class="flex justify-end">
        <Button
          @click="openStats"
          label="Avaliar estimativa"
          icon="pi pi-check"
          iconPos="right"
          class="btn-outline"
        />
      </div>
    </Dialog>
    <Dialog
      v-model:visible="showStats"
      header="Confirmar correção"
      class="custom-modal"
      :modal="true"
    >
      <div class="flex flex-col mt-4">
        <div class="my-4 gap-2">
          <div class="mb-4">
            <label>
              {{
                $t("labels.generic", {
                  pt: "Status atual de verificação de dados:",
                  en: "Current data verification status:",
                })
              }}
            </label>
            <InputText disabled class="w-full" v-model="selectedRowStatus" />
          </div>
          <div>
            <label>
              {{
                $t("labels.generic", {
                  pt: "Selecione a avaliação para os dados:",
                  en: "Select the assessment for the data:",
                })
              }}
            </label>
            <Dropdown
              class="!border-h-primary-800 !border w-full"
              optionLabel="name"
              :options="engageOptions"
              v-model="selectedDropdownValue"
            />
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-4">
          <Button
            label="Confirmar"
            icon="pi pi-check"
            class="btn-simple"
            @click="updateData"
          />
          <Button
            label="Cancelar"
            icon="pi pi-times"
            @click="closeStats"
            class="btn-delete"
          />
        </div>
      </div>
    </Dialog>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { LeanRest } from "@/services/rest/lean.rest";
import { Pagination, QueryParams } from "@/models/query-params.model";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import {
  studentInfo,
  courseInfo,
  leanColums,
  mathInfo,
  checkedInfo,
  engageOptions,
  getStatusText,
} from "./lean-colums.utils";
import { delay, take } from "rxjs";

export default defineComponent({
  name: "lean-component",
  computed: {
    rest(): LeanRest {
      return new LeanRest();
    },
  },
  data() {
    return {
      columns: leanColums,
      actions: [
        {
          title: "Ver detalhes",
          icon: "hi-chevron-right",
          type: "goTo",
          style: "btn-outline btn-mobile-right",
        },
      ],
      engageOptions,
      info: [] as any[],
      course: [] as any[],
      math: [] as any[],
      checked: [] as any[],
      data: [] as any[],
      loading: false as boolean,
      showDetails: false as boolean,
      showStats: false as boolean,
      totalRecords: 0,
      params: new QueryParams(),
      selectedRow: null as any,
      selectedRowStatus: "",
      selectedDropdownValue: null as any,
      filters: {
        search: "",
      },
      filterType: "user_name",
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    openModal(row) {
      this.info = [...studentInfo(row)];
      this.course = [...courseInfo(row)];
      this.math = [...mathInfo(row)];
      this.checked = [...checkedInfo(row)];
      this.selectedRow = row;
      this.selectedRowStatus = getStatusText(row.correct);
      this.selectedDropdownValue = engageOptions.find(
        (option) => option.value === row.correct
      );
      this.showDetails = true;
    },
    applyFilter(): void {
      this.params.search = this.filters.search;
      this.fetchData();
    },
    onFilterChange(): void {
      this.params.page = 0;
      this.applyFilter();
    },
    fetchData(): void {
      this.loading = true;
      const courseId = this.$route?.params?.courseI as string;
      this.params.course = courseId;
      this.rest
        .getResults(this.params)
        .pipe(take(1), delay(500))
        .subscribe({
          next: (response: Pagination<any>) => {
            this.data = response.results.results;
            this.totalRecords = response.results.total_count;
            this.params.total = response.results.total_count;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
    },
    paginate(event: { page: number; rows: number }): void {
      this.params.page = event.page + 1;
      this.params.perPage = event.rows;
      this.fetchData();
    },
    openStats() {
      this.showStats = true;
    },
    closeStats() {
      this.showStats = false;
    },
    updateData() {
      if (!this.selectedRow) return;
      const idValue = this.selectedRow._id.$oid || this.selectedRow._id;
      const payload = [
        {
          _id: {
            $oid: idValue,
          },
          correct: this.selectedDropdownValue?.value,
        },
      ];
      this.rest.updateStatus(payload).subscribe({
        next: () => {
          this.closeStats();
          this.showDetails = false;
          this.$toast.add(
            ToastFactory.success(Messages.STATUS_ASSESSMENT_SUCCCESS)
          );
          this.fetchData();
        },
        error: () => {
          this.$toast.add(ToastFactory.error(Messages.STATUS_ASSESSMENT_ERROR));
        },
      });
    },
  },
});
</script>


<style>
@media (max-width: 640px) {
  .btn-mobile-right {
    margin-left: auto;
  }
}
</style>